import { getInstrument, type InstrumentId, type InstrumentMetadata } from './instruments';
import { calculateParallaxError } from './parallax';
import { correctZeroError } from './zeroError';

export interface ReadingOptions {
  zeroError?: number;
  viewingAngleDeg?: number;
  parallaxScaleOffset?: number;
  random?: () => number;
  noiseEnabled?: boolean;
}

export interface InstrumentReading {
  instrumentId: InstrumentId;
  trueValue: number;
  randomError: number;
  zeroError: number;
  parallaxError: number;
  displayedReading: number;
  zeroCorrectedReading: number;
  correctedReading: number;
  uncertainty: number;
  unit: string;
}

function quantize(value: number, increment: number): number {
  const places = Math.min(12, Math.max(0, -Math.floor(Math.log10(increment)) + 2));
  const scaled = value / increment;
  const adjusted = scaled + Math.sign(scaled) * Number.EPSILON * Math.abs(scaled) * 2;
  return Number((Math.round(adjusted) * increment).toFixed(places));
}

/**
 * Simulates one instrument reading. Inject `random` in tests for reproducibility;
 * the production default draws bounded random error within the instrument uncertainty.
 */
export function getReading(
  instrumentOrId: InstrumentId | InstrumentMetadata,
  trueValue: number,
  options: ReadingOptions = {},
): InstrumentReading {
  const metadata = typeof instrumentOrId === 'string' ? getInstrument(instrumentOrId) : instrumentOrId;
  if (!Number.isFinite(trueValue)) throw new TypeError('True value must be finite.');
  if (trueValue < metadata.range.min || trueValue > metadata.range.max) {
    throw new RangeError(`True value is outside the ${metadata.name} range.`);
  }

  const random = options.random || Math.random;
  const randomValue = random();
  if (!Number.isFinite(randomValue) || randomValue < 0 || randomValue > 1) {
    throw new RangeError('Random source must return a value from 0 to 1.');
  }
  const randomError = options.noiseEnabled === false ? 0 : ((randomValue * 2) - 1) * metadata.uncertainty;
  const zeroError = options.zeroError ?? 0;
  if (!Number.isFinite(zeroError)) throw new TypeError('Zero error must be finite.');
  const parallaxError = metadata.parallaxSupported && options.viewingAngleDeg
    ? calculateParallaxError({ viewingAngleDeg: options.viewingAngleDeg, scaleOffset: options.parallaxScaleOffset ?? metadata.leastCount })
    : 0;
  const displayedReading = quantize(trueValue + randomError + zeroError + parallaxError, metadata.leastCount);
  const zeroCorrectedReading = correctZeroError(displayedReading, zeroError).correctedReading;

  return {
    instrumentId: metadata.id,
    trueValue,
    randomError,
    zeroError,
    parallaxError,
    displayedReading,
    zeroCorrectedReading,
    correctedReading: zeroCorrectedReading - parallaxError,
    uncertainty: metadata.uncertainty,
    unit: metadata.unit,
  };
}